<!-- Copyright 2025, Common Good Learning Tools LLC -->
<template>
	<v-dialog v-model="ready" content-class="k-dialog" persistent>
		<v-card v-if="ready" class="card">
			<div class="d-flex align-center ma-1">
				<v-icon class="mr-2 ml-1" :color="asset_type_color">fas {{asset_type_icon}}</v-icon>
				<div class="title" v-html="title"></div>

				<v-spacer/>

				<v-tooltip v-if="user_is_admin" bottom>
					<template v-slot:activator="{on}">
						<v-icon v-on="on" class="mr-4 mt-1" style="cursor: pointer;" small @click="find_reviewer_email">fas fa-user-magnifying-glass</v-icon>
					</template>
					Assign Reviewer
				</v-tooltip>

				<Comments :reference="local_item"/>

				<v-btn
					color="secondary"
					dark
					@click="$emit('done')"
				>
					<v-icon small class="mr-2">fas fa-times</v-icon>
					Done
				</v-btn>
			</div>

			<!-- process steps -->
			<div v-if="process_steps.length" class="my-3 px-8 d-flex flex-wrap align-center" style="justify-content: center;">
				<v-btn
					v-for="step in steps"
					class="k-tight-btn ma-2"
					:color="step.color"
					:disabled="step.disabled"
					@click="clicked(step)"
				>
					{{ step.text }}
				</v-btn>
			</div>

			<!-- alignments -->
			<div v-if="local_item.standards.length" class="d-flex flex-wrap align-center mt-3 mb-1" style="justify-content: center;">
				<span style="font-size:16px; color:#333;" class="mr-1">Alignments:</span>
				
				<CASEItemBtn
					v-for="(s, i) in local_item.standards"
					satchel_determines_framework_id="yes"
					xstar_primary_alignments="yes"
					:item="s"
					:key="s.identifier"
					:btn_color="s.btn_color(local_item.standards)"
					:outer_class="'ma-1'"
					:selected_items="local_item.standards"
					:framework_identifier="s.framework_identifier"
					small
				/>				
			</div>

			<!-- long description -->
			<div
				v-if="local_item.long_description"
				class="k-resource-collection-item-long-description mt-3 mx-auto"
				v-html="U.render_latex(local_item.long_description)"
			></div>

			<div class="my-3 d-flex flex-wrap align-center" style="justify-content: center;">
				<v-btn	
					class="k-tight-btn mx-2"
					color="primary"
					@click="open(local_item)"
				>
					Open {{ local_item.type_label(true) }}{{ type !== 'lesson' ? ' in New Window' : ''}}
					<v-icon class="ml-2" small>fas fa-arrow-up-right-from-square</v-icon>
				</v-btn>
				
				<v-btn v-if="can_edit" class="k-tight-btn mx-2" style="color: #ffffff;" color="blue darken-2" @click="edit(local_item)">
					Edit {{ type === 'lesson' ? 'Lesson' : 'Resource' }}
					<v-icon class="ml-2" small>fas fa-edit</v-icon>
				</v-btn>
			</div>

			<div v-if="metadata.length" class="k-resource-collection-item-extensions mt-3">
				<ul>
					<li v-for="datum in metadata">
						<b class="grey--text text--darken-3">{{ datum.key }}</b>
						<span :style="datum.style">{{ datum.value }}</span>
					</li>
				</ul>
			</div>

			<div
				v-if="local_item.resource_id"
				class="text-right mt-2 mr-2"
				style="font-size:10px; color:#999"
			>
				<b>Resource ID:</b> {{local_item.resource_id}}
			</div>
		</v-card>
	</v-dialog>
</template>

<script>
import { mapState } from 'vuex'
import CASEItemBtn from '../standards/CASEItemBtn'
import Comments from '../comments/Comments.vue'
import processes from './processes'

export default {
	name: 'AdminAssetPublishStatus',

	components: {CASEItemBtn, Comments},

	props: {
		item: {type: Object, required: true},
		type: {type: String, required: true},
		roles: {type: Array, required: true}
	},

	data() {
		return {
			ready: false,
			local_item: {},
			process_steps: [],
			general: {},
			can_edit: true,
			publish_status: {},
			comments: {
				show: false
			},
			assignment: {
				show: false
			},
		}
	},

	computed: {
		...mapState(['site_config', 'user_info']),

		user_is_admin() {
			return this.user_info.system_role == 'admin' || this.user_info.admin_rights.includes('su')
		},

		alignment_required() {
			return this.site_config.asset_submission_requires_alignment
		},

		title() {
			return this.type === 'lesson' ? this.local_item.lesson_title : this.local_item.description
		},

		asset_type_color() {
			let asset_colors = this.site_config.asset_type_color
			let types = Object.keys(asset_colors)
			let type = this.local_item.type
			
			switch (true) {
				case types.includes(type):
					return asset_colors[type]
				// PW: use sparkl color for google assignments
				case type === 'google' || type === 'sparkl':
					return asset_colors.sparkl_activity
				case this.local_item.teacher_facing && this.local_item.teacher_facing !== "false":
					return asset_colors.teacher_resource
				default:
					return asset_colors.student_resource
			}
		},

		asset_type_icon() {
			if (this.local_item.type === 'lesson') return 'fa-rectangle-list'

			return this.local_item.icon()
		},

		metadata() {
			let status = this.publish_status.text
			
			let metadata = [
				{
					key: "Publish Status:",
					value: status || 'Not Submitted for Publication'
				},
				{
					key: "Asset Author:",
					value: this.local_item.creator || 'Unknown'
				},
				{
					key: "Publish Status Date:",
					value: this.local_item.status_date ? new Date(this.local_item.status_date).toLocaleDateString() : ''
				},
			]

			/** Temporary for display setup - TODO remove this and replace with actual subject/grade data */
			if (this.type === 'resource'){
				let subjects = this.local_item.subject_mappings_display()
				if (subjects) {
					metadata.push({key: "Subject:", value: subjects})
				}
				let grades = this.local_item.grade_mappings_display()
				if (grades) {
					let value = grades.replaceAll(/Grade[s]*\s/g, '')
					metadata.push({key: "Grade:", value})
				}
			}

			if (this.local_item.url) {
				metadata.push({style: 'font-size: 12px;', key: "Url:", value: this.item.url})
			}

			return metadata
		},

		steps() {
			return this.process_steps.map(step => {
				step.disabled = false
				step.rules.forEach(rule => {
					if (rule(this)) step.disabled = true
				})
				return step
			})
		},
	},

	mounted() {
		/** The idea is that these process steps will be defined in processes.js and selected in site_config */
		this.local_item = this.type === 'lesson' ? new Lesson(this.item) : new Resource(this.item)
		
		this.$nextTick(() => {
			this.set_publish_status(this.local_item.publish_status)
			let process = processes[this.site_config.asset_manager_process]
			this.general = process.general
			this.roles.forEach(role => {
				this.process_steps.push(...process[role])
			})
			this.run_general_rules()
		})
	},

	methods: {
		open(item) {
			/** May need more ways of handling this */
			if (this.type === 'resource'){
				window.open(item.url)
			} else {
				this.$emit('show_lesson', item)
			}
		},

		edit(item) {
			this.$emit('edit_' + this.type, item)
		},

		clicked(step) {
			step.click(this)
			this.run_general_rules()
		},

		set_publish_status(status) {
			/** Will be an api call after the first time */
			if (!this.ready) {
				this.publish_status = status
				this.ready = true
			} else {
				let today = new Date().toJSON().slice(0, 19).replace('T', ' ')
				if (this.type === 'lesson') {
					let lesson_data = object_copy(this.local_item)
					lesson_data.publish_status = status.key
					lesson_data.status_date = today
					if (status.key === 'submitted') {
						lesson_data.submitted_date = today
					}
					if (status.final) {
						lesson_data.agency_sanctioned = 1
						lesson_data.published_date = today
					}
					let lesson_class = lesson_data.lesson_id === lesson_data.template_id ? 'template' : 'teacher'
					let payload = {
						lesson_data,
						lesson_class
					}
					
					this.$store.dispatch('save_lesson', payload)
						.then(() => {
							this.local_item.publish_status = status
							this.local_item.status_date = today
							this.item.publish_status = status
							this.item.status_date = today
							this.publish_status = status
							if (lesson_data.submitted_date) {
								this.item.submitted_date = lesson_data.submitted_date
								this.local_item.submitted_date = lesson_data.submitted_date
							}
							this.run_general_rules()
							this.$alert({text:'Status updated to: ' + status.text})
							this.$emit('status_updated', this.item, this.type)
						})
				} else {
					let resource = new Resource(object_copy(this.local_item))
					resource.publish_status = status.key
					resource.status_date = today
					if (status.key === 'submitted') {
						resource.submitted_date = today
					}
					if (status.final) {
						resource.agency_sanctioned = 1
						resource.published_date = today
					}

					this.$store.dispatch('save_resource', {resource})
						.then(() => {
							this.local_item.publish_status = status
							this.local_item.status_date = today
							this.item.publish_status = status
							this.item.status_date = today
							this.publish_status = status
							if (resource.submitted_date) {
								this.item.submitted_date = resource.submitted_date
								this.local_item.submitted_date = resource.submitted_date
							}
							this.run_general_rules()
							this.$alert({text:'Status updated to: ' + status.text})
							this.$emit('status_updated', this.item, this.type)
						})
				}
			}
		},

		run_general_rules() {
			if(this.general.rules?.length) {
				this.general.rules.forEach(rule => {
					rule(this)
				})
			}
		},

		find_reviewer_email() {
			this.$prompt({
				title: 'Assign Reviewer',
				text: 'Enter user email:',
				promptType: 'autocomplete',
				serviceName: 'email_search',
				acceptText: 'Assign',
			}).then(email => {
				// return value should be e.g. 'pepper@gmail.com (Pepper Williams)'; extract the email
				email = $.trim(email).toLowerCase().replace(/^(\S+).*/, '$1')

				if (empty(email) || email.indexOf('@') == -1) {
					this.$alert('Please enter a valid user email.')
						.then(x => { this.find_reviewer_email() })
				} else {	
					this.validate_email(email)
				}
			}).catch(n => { console.error(n) })
		},

		validate_email(email) {
			let payload = {
				user_id: this.user_info.user_id,
				email: email,
			}
			U.loading_start()
			U.ajax('admin_validate_email', payload, result=>{
				U.loading_stop()
				if (result.status != 'ok') {
					console.error('Error in admin ajax call'); vapp.ping(); return;
				}

				if (empty(result.user_id)) {
					this.$alert('We couldn’t find that email address in the system.').then(x=>{this.find_reviewer_email()})
				} else {
					this.assign_reviewer(result)
				}
			});
		},

		assign_reviewer(user) {
			let item_id = this.type == 'lesson' ? this.item.lesson_id : this.item.resource_id
			let payload = {
				user_id: this.user_info.user_id,
				user_identifier: user.user_id,
				rights_arr: ['asset_reviewer.' + this.type + '_id.' + item_id],
			}

			U.loading_start()
			U.ajax('admin_update_admin_rights', payload, result => {
				U.loading_stop()
				if (result.status != 'ok') {
					console.error('Error in admin ajax call'); vapp.ping(); return;
				} else {
					this.$alert({title:'Success!', text: user.first_name + ' ' + user.last_name + ' assigned to review this asset.'})
				}
			});
		},
	}
}
</script>

<style lang="scss">
.k-dialog {
	border-radius: 12px !important;
	width: 900px;

	.card {
		height: 100%;
		width: 100%;
		padding: 12px;

		.title {
			font-weight: bold;
			line-height: 24px;
		}
	}
}
</style>